import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, computed, contentChild, effect, inject, input,
  output, signal, untracked
} from '@angular/core';

import { IndicadorPaginasComponent } from '../indicador-paginas/indicador-paginas.component';
import {
  DURACAO_MAXIMA_CARROSSEL, DURACAO_MINIMA_CARROSSEL, FRACAO_TROCA_CARROSSEL, LIMIAR_ARRASTE_CARROSSEL, LIMIAR_VELOCIDADE_CARROSSEL,
  RESISTENCIA_BORDA_CARROSSEL
} from './utils/carrossel.constantes';

@Component({
  selector: 'app-carrossel',
  standalone: true,
  imports: [NgTemplateOutlet, IndicadorPaginasComponent],
  templateUrl: './carrossel.component.html',
  styleUrl: './carrossel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'data-ignorar-arraste-aba': '' }
})
export class CarrosselComponent implements OnInit, OnDestroy {
  readonly itens = input.required<unknown[]>();
  readonly espaco = input(12);
  readonly mostrarIndicadores = input(true);
  readonly rotuloIndicador = input('Ir para o item');
  readonly indiceMudou = output<number>();

  readonly modelo = contentChild.required(TemplateRef);
  @ViewChild('janela', { static: true }) janela!: ElementRef<HTMLElement>;

  private readonly zone = inject(NgZone);
  readonly posicao = signal(0);
  readonly indice = computed(() => Math.round(this.posicao()));
  readonly transformacao = computed(() => `translateX(calc(${-this.posicao()} * (100% + ${this.espaco()}px)))`);

  private inicio: { x: number; y: number; id: number; posicao: number } | null = null;
  private ultimo = { x: 0, t: 0 };
  private velocidade = 0;
  private horizontal = false;
  private engolirClique = false;
  private quadro = 0;
  private ultimoIndiceEmitido = 0;
  private primeiroItem: unknown;
  private remover?: () => void;

  constructor() {
    effect(() => {
      const itens = this.itens();
      untracked(() => {
        if (itens[0] !== this.primeiroItem) {
          this.primeiroItem = itens[0];
          cancelAnimationFrame(this.quadro);
          this.posicao.set(0);
        } else if (this.posicao() > itens.length - 1) {
          this.posicao.set(Math.max(0, itens.length - 1));
        }
        this.emitirIndice();
      });
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    const janela = this.janela.nativeElement;
    this.zone.runOutsideAngular(() => {
      const baixar = (e: PointerEvent) => this.aoPressionar(e);
      const mover = (e: PointerEvent) => this.aoMover(e);
      const soltar = (e: PointerEvent) => this.aoSoltar(e);
      const clique = (e: Event) => {
        if (!this.engolirClique) return;
        this.engolirClique = false;
        e.stopPropagation();
        e.preventDefault();
      };
      janela.addEventListener('pointerdown', baixar, { passive: true });
      janela.addEventListener('pointermove', mover, { passive: true });
      janela.addEventListener('pointerup', soltar, { passive: true });
      janela.addEventListener('pointercancel', soltar, { passive: true });
      janela.addEventListener('click', clique, true);
      this.remover = () => {
        janela.removeEventListener('pointerdown', baixar);
        janela.removeEventListener('pointermove', mover);
        janela.removeEventListener('pointerup', soltar);
        janela.removeEventListener('pointercancel', soltar);
        janela.removeEventListener('click', clique, true);
      };
    });
  }

  ngOnDestroy(): void {
    this.remover?.();
    cancelAnimationFrame(this.quadro);
  }

  irPara(indice: number): void {
    this.animarAte(this.limitar(indice), 0);
  }

  anterior(): void {
    this.irPara(this.indice() - 1);
  }

  proximo(): void {
    this.irPara(this.indice() + 1);
  }

  private aoPressionar(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (this.itens().length < 2) return;
    cancelAnimationFrame(this.quadro);
    this.inicio = { x: e.clientX, y: e.clientY, id: e.pointerId, posicao: this.posicao() };
    this.ultimo = { x: e.clientX, t: e.timeStamp };
    this.velocidade = 0;
    this.horizontal = false;
    this.engolirClique = false;
  }

  private aoMover(e: PointerEvent): void {
    const inicio = this.inicio;
    if (!inicio || inicio.id !== e.pointerId) return;
    const dx = e.clientX - inicio.x;
    const dy = e.clientY - inicio.y;
    if (!this.horizontal) {
      if (Math.abs(dy) > LIMIAR_ARRASTE_CARROSSEL && Math.abs(dy) > Math.abs(dx)) {
        this.inicio = null;
        this.animarAte(this.limitar(Math.round(this.posicao())), 0);
        return;
      }
      if (Math.abs(dx) < LIMIAR_ARRASTE_CARROSSEL) return;
      this.horizontal = true;
      this.janela.nativeElement.setPointerCapture(e.pointerId);
    }
    const intervalo = e.timeStamp - this.ultimo.t;
    if (intervalo > 0) this.velocidade = (e.clientX - this.ultimo.x) / intervalo;
    this.ultimo = { x: e.clientX, t: e.timeStamp };

    const bruta = inicio.posicao - dx / this.passo();
    const maximo = this.itens().length - 1;
    const posicao = bruta < 0
      ? bruta * RESISTENCIA_BORDA_CARROSSEL
      : bruta > maximo ? maximo + (bruta - maximo) * RESISTENCIA_BORDA_CARROSSEL : bruta;
    this.posicao.set(posicao);
    this.emitirIndice();
  }

  private aoSoltar(e: PointerEvent): void {
    const inicio = this.inicio;
    if (!inicio || inicio.id !== e.pointerId) return;
    this.inicio = null;
    if (!this.horizontal) return;
    this.horizontal = false;
    this.engolirClique = true;
    setTimeout(() => (this.engolirClique = false), 300);

    const deslocamento = this.posicao() - inicio.posicao;
    let destino = Math.round(this.posicao());
    if (destino === Math.round(inicio.posicao)) {
      const rapido = Math.abs(this.velocidade) >= LIMIAR_VELOCIDADE_CARROSSEL;
      if (rapido) destino -= Math.sign(this.velocidade);
      else if (Math.abs(deslocamento) >= FRACAO_TROCA_CARROSSEL) destino += Math.sign(deslocamento);
    }
    this.animarAte(this.limitar(destino), Math.abs(this.velocidade) / this.passo());
  }

  private animarAte(destino: number, velocidadeInicial: number): void {
    cancelAnimationFrame(this.quadro);
    const origem = this.posicao();
    const distancia = destino - origem;
    if (Math.abs(distancia) < 0.001 || this.movimentoReduzido()) {
      this.posicao.set(destino);
      this.emitirIndice();
      return;
    }
    const base = Math.min(DURACAO_MAXIMA_CARROSSEL, DURACAO_MINIMA_CARROSSEL + Math.abs(distancia) * 160);
    const duracao = velocidadeInicial > 0
      ? Math.max(DURACAO_MINIMA_CARROSSEL, Math.min(base, (Math.abs(distancia) / velocidadeInicial) * 3))
      : base;
    const comeco = performance.now();
    this.zone.runOutsideAngular(() => {
      const quadro = (agora: number) => {
        const t = Math.min(1, (agora - comeco) / duracao);
        this.posicao.set(origem + distancia * (1 - Math.pow(1 - t, 3)));
        this.emitirIndice();
        if (t < 1) this.quadro = requestAnimationFrame(quadro);
      };
      this.quadro = requestAnimationFrame(quadro);
    });
  }

  private emitirIndice(): void {
    const indice = this.limitar(this.indice());
    if (indice === this.ultimoIndiceEmitido) return;
    this.ultimoIndiceEmitido = indice;
    this.zone.run(() => this.indiceMudou.emit(indice));
  }

  private passo(): number {
    return (this.janela.nativeElement.clientWidth || 1) + this.espaco();
  }

  private limitar(indice: number): number {
    return Math.max(0, Math.min(this.itens().length - 1, indice));
  }

  private movimentoReduzido(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }
}
